import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControlLabel,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import classNames from 'classnames';
import theme, { colors } from 'theme';
import {
  DialogWidth,
  Expedition,
  ExpeditionBuyerConfirmation,
  ExpeditionRepartitionTonnageRepreneurFormData,
} from 'types';
import { roundToPrecision } from 'services/math';
import { formatPercentage } from 'utils/number';
import { formatTons, formatTonsFromKg } from 'utils/weight';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { computeRepartitionPercentageFromWeight, getTonnageReprisInTons } from 'services/expedition';
import { transformObjectId } from 'services/apiPlatformTransformer';
import TableSeparatorRow from 'components/TableSeparatorRow';
import MemoClosureErrorIcon from 'components/Icons/ClosureErrorIcon';
import CustomSwitch from 'components/Buttons/CustomSwitch';
import { AppContext } from 'context';
import { useFirstAndLastClosureDates } from '../../utils/closureHooks';
import NewDialog from '../NewDialog';
import { ValidateIcon } from '../Icons';

const useStyles = makeStyles({
  colClient: {
    width: '40%',
  },
  colData: {
    width: '15%',
  },
  closeIcon: {
    width: '16px',
  },
  headerRow: {
    '& th': {
      borderWidth: '2px',
      '&:first-child': {
        paddingLeft: '40px',
      },
      '&:last-child': {
        paddingRight: '30px',
      },
    },
  },
  muiTextFieldRoot: {
    '& input': {
      textAlign: 'right',
      paddingTop: 12,
    },
  },
  buyerConfirmationTab: {
    display: 'flex',
    maxWidth: '90%',
    justifyContent: 'space-between',
    marginBottom: '36px',
    paddingTop: '28px',
  },
  dataRow: {
    '& td': {
      borderBottom: 'none',
      '&:first-child': {
        paddingLeft: '40px',
      },
      '&:last-child': {
        paddingRight: '30px',
      },
    },
  },
  smallTablePaper: {
    width: '100%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  error: {
    color: colors.red,
  },
  titleContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'left',
    marginTop: '-30px',
  },
  dialogContainer: {
    width: '100%',
    paddingRight: '48px',
  },
  warningIcon: {
    width: '10%',
    color: colors.darkOrange,
    margin: '0px 13px 0px -2px',
  },
  title: {
    fontSize: '18px',
    lineHeight: '28px',
    fontWeight: 600,
  },
  footer: {
    display: 'flex',
    flexFlow: 'column',
    textAlign: 'left',
    padding: '0px',
  },
  bottomButton: {
    textAlign: 'right',
  },
  continueIcon: {
    width: '16px',
  },
  continueButton: {
    backgroundColor: '#2F3741',
    color: '#FFFFFF',
    marginLeft: theme.spacing(2),

    '&:hover': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
    '&:active': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  miseEnCoherenceMessage: {
    textAlign: 'right',
    paddingRight: '10px',
  },
});

type ExpeditionRepartitionAccepteeRepreneurPathTonnage =
  `expeditionRepartitionAccepteeRepreneur.${number}.tonnageRepris`;

export interface ExpeditionRepartitionTonnageRepreneurFormProps {
  onSubmit: SubmitHandler<ExpeditionRepartitionTonnageRepreneurFormData>;
  isEditMode: boolean;
  formName: string;
  expedition: Expedition;
  expeditionBuyerConfirmation: ExpeditionBuyerConfirmation | null;
  expeditionClientPortionWeightPercentLabels: { [key: string]: string };
  setDisabledSaveButton: (isDisabled: boolean) => void;
  currentTonnageAccepteInTons: number;
  isAutoTonnageRepris?: boolean;
  setIsAutoTonnageRepris: (current: boolean) => void;
}

const ExpeditionRepartitionTonnageRepreneurForm: React.FC<ExpeditionRepartitionTonnageRepreneurFormProps> = ({
  onSubmit,
  isEditMode,
  expedition,
  formName,
  expeditionBuyerConfirmation,
  expeditionClientPortionWeightPercentLabels,
  setDisabledSaveButton,
  currentTonnageAccepteInTons,
  isAutoTonnageRepris,
  setIsAutoTonnageRepris,
}) => {
  const classes = useStyles();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'expeditions.show.buyerConfirmationTab' });
  const { t } = useTranslation();
  const [pourcentageReprisWithDeltaComputed, setPourcentageReprisWithDeltaComputed] = useState<{
    [key: string]: string;
  }>({});
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm<ExpeditionRepartitionTonnageRepreneurFormData>({
    mode: 'onBlur',
  });
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const expeditionClientPortionList = expedition.expeditionClientPortions;
  const expeditionClientPortionTotal = expeditionClientPortionList.length;
  const expeditionConfirmedClientPortionList = expedition.expeditionConfirmedClientPortions;
  const [tonnageAccepteInKilos, setTonnageAccepteInKilos] = useState(0);
  const hasExpeditionConfirmedClientPortion = expeditionConfirmedClientPortionList.length <= 0;
  const tonnageReprisToWatch =
    expeditionClientPortionList?.map(
      (_, index): ExpeditionRepartitionAccepteeRepreneurPathTonnage =>
        `expeditionRepartitionAccepteeRepreneur.${index}.tonnageRepris`,
    ) ?? [];
  const watchTonnageRepris = watch(tonnageReprisToWatch);
  const totalTonnageRepris: number = watchTonnageRepris?.reduce((total, newWeight): number => {
    const weight = 'string' === typeof newWeight ? parseFloat(newWeight) : newWeight;
    return total + (weight ? weight * 1000 : 0);
  }, 0);
  const shouldCallApiForComputePercentageFromWeight =
    isEditMode && currentTonnageAccepteInTons * 1000 === totalTonnageRepris;
  const shouldDisplayForm =
    expeditionBuyerConfirmation && expeditionClientPortionTotal > 0 && null !== expeditionClientPortionList;
  const isErrorTotalTonnageReprisDiffTotalTonnageAccepte = isEditMode && totalTonnageRepris !== tonnageAccepteInKilos;
  const firstAndLastClosureDates = useFirstAndLastClosureDates();
  const lastClosureDates = firstAndLastClosureDates ? firstAndLastClosureDates.lastClosureDate : null;
  const lastDayOfLastClosureDates =
    lastClosureDates &&
    new Date(new Date(lastClosureDates.getFullYear(), lastClosureDates.getMonth() + 1, 0).setHours(23, 59, 59));
  const expeditionDate = new Date(expedition.expeditionDate);
  const isExpeditionInClosedMonth = lastDayOfLastClosureDates && lastDayOfLastClosureDates >= expeditionDate;

  const isPercentagesDifferent = expeditionClientPortionList.find((expeditionClientPortion, index) => {
    const pourcentageExpedieInteger = Math.round(
      (expeditionClientPortion?.percentage ??
        parseFloat(expeditionClientPortionWeightPercentLabels[index]) / 100 ??
        0) * 1000000,
    );

    const pourcentageReprisInteger = Math.round(
      (getValues(`expeditionRepartitionAccepteeRepreneur.${index}.pourcentageRepris`)
        ? getValues(`expeditionRepartitionAccepteeRepreneur.${index}.pourcentageRepris`)
        : 0) * 1000000,
    );

    return pourcentageExpedieInteger !== pourcentageReprisInteger;
  });

  const isTonnagesDifferent = expeditionClientPortionList.find((expeditionClientPortion, index) => {
    const tonnageExpedieInteger = Math.round(expeditionClientPortion.weight);

    const tonnageReprisInteger = Math.round(
      (getValues(`expeditionRepartitionAccepteeRepreneur.${index}.tonnageRepris`)
        ? getValues(`expeditionRepartitionAccepteeRepreneur.${index}.tonnageRepris`)
        : 0) * 1000,
    );

    return tonnageExpedieInteger !== tonnageReprisInteger;
  });

  const shouldDisplayMiseEnCoherenceMessage =
    isEditMode &&
    isExpeditionInClosedMonth &&
    isPercentagesDifferent &&
    true === expedition.isUpdatedRepartitionAccepteeRepreneur &&
    false === expedition.isMiseEnCoherenceAccepted;

  const [isOpenDialogMiseEnCoherenceMoisClos, setIsOpenDialogMiseEnCoherenceMoisClos] = useState(false);
  const [isOpenDialogMiseEnCoherenceMoisNonClos, setIsOpenDialogMiseEnCoherenceMoisNonClos] = useState(false);
  const [isOpenDialogForcerMiseEnCoherence, setIsOpenDialogForcerMiseEnCoherence] = useState(false);

  const openConfirmationOverlay = (e: FormEvent) => {
    e.preventDefault();
    setIsOpenDialogMiseEnCoherenceMoisClos(true);
  };

  useEffect(() => {
    if (!isAutoTonnageRepris) return;

    let maxTonnageRepris = 0;
    let sumTonnageRepris = 0;
    let indexMaxTonnageRepris = 0;
    expeditionClientPortionList.map((expeditionClientPortion, index) => {
      const pourcentageRepris = getValues(`expeditionRepartitionAccepteeRepreneur.${index}.pourcentageRepris`);
      const tonnageReprisInTons = roundToPrecision(Number(pourcentageRepris) * currentTonnageAccepteInTons);
      sumTonnageRepris = sumTonnageRepris + tonnageReprisInTons;

      if (tonnageReprisInTons > maxTonnageRepris) {
        maxTonnageRepris = tonnageReprisInTons;
        indexMaxTonnageRepris = index;
      }
      setValue(`expeditionRepartitionAccepteeRepreneur.${index}.tonnageRepris`, tonnageReprisInTons);
      return null;
    });

    if (sumTonnageRepris !== currentTonnageAccepteInTons) {
      const diff = currentTonnageAccepteInTons - sumTonnageRepris;
      setValue(
        `expeditionRepartitionAccepteeRepreneur.${indexMaxTonnageRepris}.tonnageRepris`,
        maxTonnageRepris + diff,
      );
    } // see ET-2546 for more informations
  }, [isAutoTonnageRepris, currentTonnageAccepteInTons]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTonnageAccepteInKilos(currentTonnageAccepteInTons * 1000);
  }, [currentTonnageAccepteInTons]);

  useEffect(() => {
    setDisabledSaveButton(isErrorTotalTonnageReprisDiffTotalTonnageAccepte || 0 >= currentTonnageAccepteInTons);
  }, [isErrorTotalTonnageReprisDiffTotalTonnageAccepte, currentTonnageAccepteInTons, setDisabledSaveButton]);

  useEffect(() => {
    if (isEditMode || 0 === tonnageAccepteInKilos || !shouldDisplayForm) return;

    const tonnageReprisInKilosToSend: string[] = [];

    expeditionClientPortionList.map((expeditionClientPortion, index) => {
      const expeditionConfirmedClientPortion = hasExpeditionConfirmedClientPortion
        ? null
        : expeditionConfirmedClientPortionList?.find(
            expeditionConfirmedClientPortion =>
              expeditionConfirmedClientPortion.client['@id'] === expeditionClientPortion.client['@id'],
          );

      const tonnageReprisInTons = getTonnageReprisInTons(expeditionClientPortion, expeditionConfirmedClientPortion);

      const tonnageReprisInKilos = tonnageReprisInTons ? tonnageReprisInTons * 1000 : 0;

      tonnageReprisInKilosToSend.push(tonnageReprisInKilos.toString());
      setValue(`expeditionRepartitionAccepteeRepreneur.${index}.tonnageRepris`, tonnageReprisInTons);
      return null;
    });

    computeRepartitionPercentageFromWeight(tonnageAccepteInKilos, tonnageReprisInKilosToSend, 4).then(data => {
      const repartitionEnPourcentage = data?.repartitionEnPourcentage?.['hydra:member'] ?? [];
      setPourcentageReprisWithDeltaComputed(repartitionEnPourcentage);
    }); // see ET-2546 for more informations
  }, [isEditMode, tonnageAccepteInKilos, shouldDisplayForm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldCallApiForComputePercentageFromWeight) {
      const tonnageReprisInTons: string[] = [];

      tonnageReprisToWatch.forEach((tonnageReprisWatched, index) => {
        const tonnageReprisInKilos = getValues(tonnageReprisWatched) ? getValues(tonnageReprisWatched) * 1000 : 0;
        tonnageReprisInTons.push(tonnageReprisInKilos.toString());
      });

      computeRepartitionPercentageFromWeight(tonnageAccepteInKilos, tonnageReprisInTons, 4).then(data => {
        setPourcentageReprisWithDeltaComputed(data?.repartitionEnPourcentage?.['hydra:member'] ?? []);
      });
    } // see ET-2546 for more informations
  }, [shouldCallApiForComputePercentageFromWeight, expedition]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {shouldDisplayForm && (
        <Box className={classes.buyerConfirmationTab}>
          <form
            id={formName}
            onSubmit={e => {
              e.preventDefault();

              if (expedition.isUpdatedRepartitionAccepteeRepreneur) {
                if (shouldDisplayMiseEnCoherenceMessage) {
                  if (true === getValues('isForceMiseEnCoherence')) {
                    setIsOpenDialogForcerMiseEnCoherence(true);
                  } else {
                    setValue('isMiseEnCoherence', false);
                    handleSubmit(onSubmit)();
                  }
                } else if (isDirty && isPercentagesDifferent && isTonnagesDifferent) {
                  if (isExpeditionInClosedMonth) {
                    openConfirmationOverlay(e);
                  } else {
                    setIsOpenDialogMiseEnCoherenceMoisNonClos(true);
                  }
                } else {
                  setValue('isMiseEnCoherence', null);
                  handleSubmit(onSubmit)();
                }
              } else {
                if (isDirty && isExpeditionInClosedMonth && isPercentagesDifferent && isTonnagesDifferent) {
                  openConfirmationOverlay(e);
                } else if (isDirty && !isExpeditionInClosedMonth && isPercentagesDifferent && isTonnagesDifferent) {
                  setIsOpenDialogMiseEnCoherenceMoisNonClos(true);
                } else {
                  setValue('isMiseEnCoherence', null);
                  handleSubmit(onSubmit)();
                }
              }
            }}
            className={classes.buyerConfirmationTab}
          >
            <NewDialog
              closable={true}
              isOpen={isOpenDialogMiseEnCoherenceMoisClos}
              onClose={() => setIsOpenDialogMiseEnCoherenceMoisClos(false)}
              width={DialogWidth.LARGE_FORM}
              title={''}
              renderContent={() => (
                <>
                  <div className={classes.dialogContainer}>
                    <div className={classes.titleContainer}>
                      <Typography className={classes.title}>{pt('dialog.update.subject')}</Typography>
                    </div>
                    <br />
                    <div>{pt('dialog.update.content')}</div>
                    <br />
                    <br />
                  </div>
                </>
              )}
              renderFooter={() => (
                <div className={classes.bottomButton}>
                  {sortingCenter?.isActivatedMiseEnCoherenceObligatoire ? (
                    <Button
                      variant="outlined"
                      startIcon={<MemoClosureErrorIcon className={classes.closeIcon} />}
                      onClick={() => setIsOpenDialogMiseEnCoherenceMoisClos(false)}
                    >
                      {pt('dialog.update.cancel')}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setValue('isMiseEnCoherence', false);
                        handleSubmit(onSubmit)();
                      }}
                    >
                      {pt('dialog.update.refused')}
                    </Button>
                  )}
                  <Button
                    className={classes.continueButton}
                    variant="outlined"
                    endIcon={<ValidateIcon className={classes.continueIcon} />}
                    onClick={() => {
                      setValue(
                        'isOneClosure',
                        expeditionDate?.getFullYear() === lastDayOfLastClosureDates?.getFullYear() &&
                          expeditionDate?.getMonth() === lastDayOfLastClosureDates?.getMonth(),
                      );
                      setValue('year', expeditionDate?.getFullYear().toString());
                      setValue('month', expeditionDate?.toLocaleString('fr', { month: 'long' }));
                      setValue('isMiseEnCoherence', true);
                      handleSubmit(onSubmit)();
                    }}
                  >
                    {pt('dialog.update.continue')}
                  </Button>
                </div>
              )}
            />
            <NewDialog
              closable={true}
              isOpen={isOpenDialogMiseEnCoherenceMoisNonClos}
              onClose={() => setIsOpenDialogMiseEnCoherenceMoisNonClos(false)}
              width={DialogWidth.LARGE_FORM}
              title={''}
              renderContent={() => (
                <>
                  <div className={classes.dialogContainer}>
                    <div className={classes.titleContainer}>
                      <Typography className={classes.title}>
                        {pt('dialog.miseEnCoherenceMoisNonCloture.subject')}
                      </Typography>
                    </div>
                    <br />
                    <div>{pt('dialog.miseEnCoherenceMoisNonCloture.content')}</div>
                    <br />
                    <br />
                  </div>
                </>
              )}
              renderFooter={() => (
                <div className={classes.bottomButton}>
                  <Button variant="outlined" onClick={() => setIsOpenDialogMiseEnCoherenceMoisNonClos(false)}>
                    {pt('dialog.miseEnCoherenceMoisNonCloture.cancel')}
                  </Button>
                  <Button
                    className={classes.continueButton}
                    variant="outlined"
                    endIcon={<ValidateIcon className={classes.continueIcon} />}
                    onClick={() => {
                      setValue('isMiseEnCoherence', true);
                      handleSubmit(onSubmit)();
                    }}
                  >
                    {pt('dialog.miseEnCoherenceMoisNonCloture.continue')}
                  </Button>
                </div>
              )}
            />
            <NewDialog
              closable={true}
              isOpen={isOpenDialogForcerMiseEnCoherence}
              onClose={() => setIsOpenDialogForcerMiseEnCoherence(false)}
              width={DialogWidth.LARGE_FORM}
              title={''}
              renderContent={() => (
                <>
                  <div className={classes.dialogContainer}>
                    <div className={classes.titleContainer}>
                      <Typography className={classes.title}>{pt('dialog.forcerMiseEnCoherence.subject')}</Typography>
                    </div>
                    <br />
                    <div>{pt('dialog.forcerMiseEnCoherence.content')}</div>
                    <br />
                    <br />
                  </div>
                </>
              )}
              renderFooter={() => (
                <div className={classes.bottomButton}>
                  <Button
                    variant="outlined"
                    startIcon={<MemoClosureErrorIcon className={classes.closeIcon} />}
                    onClick={() => setIsOpenDialogForcerMiseEnCoherence(false)}
                  >
                    {pt('dialog.forcerMiseEnCoherence.cancel')}
                  </Button>
                  <Button
                    className={classes.continueButton}
                    variant="outlined"
                    endIcon={<ValidateIcon className={classes.continueIcon} />}
                    onClick={() => {
                      setValue(
                        'isOneClosure',
                        expeditionDate?.getFullYear() === lastDayOfLastClosureDates?.getFullYear() &&
                          expeditionDate?.getMonth() === lastDayOfLastClosureDates?.getMonth(),
                      );
                      setValue('year', expeditionDate?.getFullYear().toString());
                      setValue('month', expeditionDate?.toLocaleString('fr', { month: 'long' }));
                      setValue('isMiseEnCoherence', true);
                      handleSubmit(onSubmit)();
                    }}
                  >
                    {pt('dialog.forcerMiseEnCoherence.continue')}
                  </Button>
                </div>
              )}
            />
            <div className={classes.formContainer}>
              {shouldDisplayMiseEnCoherenceMessage && (
                <div className={classes.miseEnCoherenceMessage}>
                  <p>{pt('forceMiseEnCoherenceMessage.firstLine')}</p>
                  <p>
                    <Controller
                      name="isForceMiseEnCoherence"
                      control={control}
                      render={({ field: { ref, value, ...fieldProps } }) => (
                        <FormControlLabel
                          id="isForceMiseEnCoherence"
                          disabled={false}
                          value="start"
                          control={<CustomSwitch checked={value} {...fieldProps} />}
                          label={pt('forceMiseEnCoherenceMessage.secondLine')}
                          labelPlacement="start"
                          inputRef={ref}
                        />
                      )}
                    />
                  </p>
                </div>
              )}
              <Paper square className={classes.smallTablePaper}>
                <Box display="flex" paddingLeft="21px" paddingRight="21px">
                  <Table>
                    <TableHead>
                      <TableRow className={classes.headerRow}>
                        <TableCell className={classes.colClient}>{pt('client')}</TableCell>
                        <TableCell className={classes.colData}>{pt('shippedWeight')}</TableCell>
                        <TableCell className={classes.colData}>{pt('shippedPercent')}</TableCell>
                        <TableCell className={classes.colData}>{pt('acceptedPercent')}</TableCell>
                        <TableCell className={classes.colData}>{pt('acceptedWeight')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expeditionClientPortionList.map((expeditionClientPortion, index) => {
                        let pourcentageExpedie = 0;

                        if (expedition.isMiseEnCoherenceAccepted) {
                          pourcentageExpedie =
                            expeditionClientPortion.expeditionRepartitionAccepteeRepreneur?.pourcentageRepris ?? 0;
                        } else if (expeditionClientPortion.percentage) {
                          pourcentageExpedie = expeditionClientPortion.percentage ?? 0;
                        } else {
                          pourcentageExpedie = parseFloat(expeditionClientPortionWeightPercentLabels[index]) / 100 ?? 0;
                        }

                        const expeditionConfirmedClientPortion = hasExpeditionConfirmedClientPortion
                          ? null
                          : expeditionConfirmedClientPortionList?.find(
                              expeditionConfirmedClientPortion =>
                                expeditionConfirmedClientPortion.client['@id'] ===
                                expeditionClientPortion.client['@id'],
                            );

                        const expeditionRepartitionTonnageRepreneurId = transformObjectId(
                          expeditionClientPortion.expeditionRepartitionAccepteeRepreneur,
                        );

                        const tonnageReprisInTons = getTonnageReprisInTons(
                          expeditionClientPortion,
                          expeditionConfirmedClientPortion,
                        );

                        let pourcentageReprisComputed = 0;

                        if (
                          !isEditMode &&
                          !expeditionClientPortion.isUpdatedRepartitionAccepteeRepreneur &&
                          (currentTonnageAccepteInTons * 1000 === expedition.weight || isTonnagesDifferent)
                        ) {
                          pourcentageReprisComputed = pourcentageExpedie;
                        } else if (
                          !isEditMode &&
                          expeditionClientPortion.isUpdatedRepartitionAccepteeRepreneur &&
                          isTonnagesDifferent
                        ) {
                          pourcentageReprisComputed =
                            expeditionClientPortion.expeditionRepartitionAccepteeRepreneur?.pourcentageRepris ?? 0;
                        } else if (
                          (!isEditMode && isTonnagesDifferent) ||
                          (isDirty && !isAutoTonnageRepris && shouldCallApiForComputePercentageFromWeight)
                        ) {
                          pourcentageReprisComputed =
                            parseFloat(pourcentageReprisWithDeltaComputed[index] ?? '0') / 100;
                        } else if (
                          isEditMode &&
                          !isAutoTonnageRepris &&
                          isTonnagesDifferent &&
                          (isDirty ||
                            (expeditionClientPortion.isUpdatedRepartitionAccepteeRepreneur &&
                              false === expedition.isMiseEnCoherenceAccepted))
                        ) {
                          const tonnageReprisForComputeInKilos =
                            (watchTonnageRepris[index] ?? tonnageReprisInTons) * 1000;
                          pourcentageReprisComputed = tonnageReprisForComputeInKilos / tonnageAccepteInKilos;
                        } else if (
                          isEditMode &&
                          !isAutoTonnageRepris &&
                          !expeditionClientPortion.isUpdatedRepartitionAccepteeRepreneur &&
                          isTonnagesDifferent
                        ) {
                          pourcentageReprisComputed = pourcentageExpedie;
                        } else if (isEditMode && isAutoTonnageRepris) {
                          pourcentageReprisComputed =
                            expeditionClientPortion.expeditionRepartitionAccepteeRepreneur?.pourcentageRepris ??
                            pourcentageExpedie;
                        } else {
                          pourcentageReprisComputed = pourcentageExpedie;
                        }

                        setValue(
                          `expeditionRepartitionAccepteeRepreneur.${index}.id`,
                          expeditionRepartitionTonnageRepreneurId,
                        );

                        setValue(
                          `expeditionRepartitionAccepteeRepreneur.${index}.pourcentageRepris`,
                          pourcentageReprisComputed,
                        );
                        setValue(
                          `expeditionRepartitionAccepteeRepreneur.${index}.expeditionClientPortion`,
                          expeditionClientPortion['@id'],
                        );

                        return (
                          <TableRow className={classes.dataRow}>
                            <TableCell>{expeditionClientPortion.client.shortName}</TableCell>
                            <TableCell>{formatTonsFromKg(expeditionClientPortion.weight)}</TableCell>
                            <TableCell>{formatPercentage(pourcentageExpedie, 4)}</TableCell>
                            <TableCell>{formatPercentage(pourcentageReprisComputed, 4)}</TableCell>
                            {isEditMode ? (
                              <Controller
                                name={`expeditionRepartitionAccepteeRepreneur.${index}.tonnageRepris`}
                                control={control}
                                shouldUnregister
                                render={({ field: { ref, ...inputProps }, fieldState }) => (
                                  <WeightInputField
                                    onFocus={() => setIsAutoTonnageRepris(false)}
                                    className={classes.muiTextFieldRoot}
                                    inputRef={ref}
                                    {...inputProps}
                                    helperText={fieldState.error?.message}
                                    error={fieldState.invalid}
                                  />
                                )}
                              />
                            ) : (
                              <TableCell>{formatTons(tonnageReprisInTons)}</TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      <TableSeparatorRow greyColorScheme isError={isErrorTotalTonnageReprisDiffTotalTonnageAccepte}>
                        {t('expeditions.total')}
                      </TableSeparatorRow>
                      <TableRow className={classes.dataRow}>
                        <TableCell></TableCell>
                        <TableCell>{formatTonsFromKg(expedition.weight)}</TableCell>
                        <TableCell>{formatPercentage(1, 4)}</TableCell>
                        <TableCell>{formatPercentage(totalTonnageRepris / tonnageAccepteInKilos, 4)}</TableCell>
                        <TableCell
                          className={classNames({
                            [classes.error]: isErrorTotalTonnageReprisDiffTotalTonnageAccepte,
                          })}
                        >
                          {formatTonsFromKg(totalTonnageRepris)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </div>
          </form>
        </Box>
      )}
    </>
  );
};
export default ExpeditionRepartitionTonnageRepreneurForm;
